import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ProbeLogo } from "@/components/ProbeLogo";
import { ThemeToggle } from "@/components/ThemeToggle";

// When a footer link points to the current pathname (no hash), React Router
// will not re-render and ScrollToTop won't fire — so we force a scroll.
const handleSamePathScroll = (to, currentPath) => (e) => {
  if (!to || to.includes("#")) return;
  if (to === currentPath) {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const NAV = [
  { label: "Home", to: "/" },
  { label: "Features", to: "/#features" },
  { label: "How it works", to: "/#how" },
  { label: "Pricing", to: "/#pricing" },
];

const FOOTER_COLS = [
  {
    heading: "Product",
    links: [
      { label: "Home", to: "/" },
      { label: "Features", to: "/#features" },
      { label: "How it works", to: "/#how" },
      { label: "Pricing", to: "/#pricing" },
      { label: "Sign in", to: "/login" },
      { label: "Start testing free", to: "/signup" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy Policy", to: "/legal/privacy" },
      { label: "Terms of Service", to: "/legal/terms" },
      { label: "DPA", to: "/legal/dpa" },
    ],
  },
];

export function MarketingShell({ children, variant = "light" }) {
  const location = useLocation();
  const isDark = variant === "dark";
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu on route or hash change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Lock body scroll while the slide-down menu is open
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <div
      className={`min-h-screen flex flex-col ${
        isDark
          ? "bg-[#0B0B0C] text-white"
          : "bg-white text-[#0F172A] dark:bg-[#0B0B0C] dark:text-white"
      }`}
      data-testid="marketing-shell"
      data-variant={variant}
      data-marketing="true"
    >
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0B0B0C]/85 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 sm:gap-6">
          <Link to="/" className="shrink-0" data-testid="marketing-home-link" aria-label="Probe home">
            <ProbeLogo size="md" variant="light" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1" data-testid="marketing-nav">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 h-9 inline-flex items-center text-sm font-medium text-white/70 hover:text-white transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <ThemeToggle variant="marketing" />
            <Link
              to="/login"
              data-testid="marketing-signin-link"
              className="inline-flex items-center h-9 px-3 text-sm font-medium text-white/75 hover:text-white transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              data-testid="marketing-get-started-button"
              className="hidden lg:inline-flex items-center gap-1.5 bg-[#3B82F6] text-white h-9 px-4 rounded-md text-sm font-medium hover:bg-[#2563EB] transition-colors"
            >
              Start testing free
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              data-testid="marketing-burger-button"
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/10 border border-white/10 text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>

        {/* Mobile / tablet slide-down menu */}
        <div
          className={`lg:hidden overflow-hidden border-t border-white/[0.06] bg-[#0B0B0C]/95 backdrop-blur-md transition-[max-height,opacity] duration-200 ${
            menuOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
          }`}
          data-testid="marketing-mobile-menu"
          aria-hidden={!menuOpen}
        >
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-3 rounded-md text-sm font-medium text-white/80 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/signup"
              data-testid="mobile-marketing-get-started-button"
              className="mt-2 inline-flex items-center justify-center gap-1.5 bg-[#3B82F6] text-white h-11 px-4 rounded-md text-sm font-semibold hover:bg-[#2563EB] transition-colors"
            >
              Start testing free
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#030712] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
          <div className="space-y-3 sm:col-span-1">
            <ProbeLogo size="md" variant="light" />
            <p className="text-sm text-white/55 max-w-xs leading-relaxed">
              Test your web application using AI agents.
            </p>
          </div>
          {FOOTER_COLS.map((col) => (
            <div key={col.heading}>
              <p className="font-mono text-[10px] tracking-[0.2em] text-white/40 uppercase mb-3">{col.heading}</p>
              <ul className="space-y-2.5">
                {col.links.map((l) =>
                  l.to ? (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        onClick={handleSamePathScroll(l.to, location.pathname)}
                        className="text-sm text-white/65 hover:text-white transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={l.label}>
                      <a href={l.href || "#"} className="text-sm text-white/65 hover:text-white transition-colors">
                        {l.label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} Probe by FastCollab Systems Private Limited. All rights reserved.
            </p>
            <a
              href="mailto:support@helloprobe.com"
              data-testid="footer-support-email"
              className="text-xs text-white/50 hover:text-white transition-colors"
            >
              support@helloprobe.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
